import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Snackbar,
    Alert,
    Chip,
    TextField,
    InputAdornment,
    Tooltip,
    IconButton,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useTablePagination, useAppMeta } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import { useLazyGetBranchCOCUserDetailQuery } from "store/services/reportApi";
import moment from "moment";
import { Download, OpenInNew, Search } from "@mui/icons-material";
import { exportExcelFile } from "utils/exportExcelFile";

const paymentStatusColor = (status) => {
    switch (status) {
        case "Paid":
            return "success";
        case "Not Paid":
            return "error";
        case "Collected":
            return "warning";
        default:
            return "default";
    }
};

const BranchCOCUserDetail = () => {
    const [searchParams] = useSearchParams();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();
    const branchId = searchParams.get("branch");
    const branchName = searchParams.get("branchName") || "Branch";
    const userId = searchParams.get("userId");
    const userName = searchParams.get("userName") || "User";
    const pmProvinceId = searchParams.get("pmProvinceId");
    const pmProvinceName = searchParams.get("pmProvinceName") || "";
    const [keyword, setKeyword] = useState("");
    const [debouncedKeyword, setDebouncedKeyword] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedKeyword(keyword), 400);
        return () => clearTimeout(timer);
    }, [keyword]);
    const [errorMessage, setErrorMessage] = useState("");
    const provinceListPath = `/app/crm/reports/coc-branch-report?mode=provinces&pmLevel=branches&pmProvinceId=${pmProvinceId || ""}&pmProvinceName=${encodeURIComponent(pmProvinceName)}`;
    const summaryPath = `/app/crm/reports/branch-coc-user-summary?branch=${branchId}&branchName=${encodeURIComponent(branchName)}${pmProvinceId ? `&pmProvinceId=${pmProvinceId}&pmProvinceName=${encodeURIComponent(pmProvinceName)}` : ""}`;
    const breadcrumbs = [
        { title: "Reports", path: "/app/crm/reports/list" },
        { title: "Pending Branch COC Report", path: "/app/crm/reports/coc-branch-report" },
        ...(pmProvinceId
            ? [{ title: pmProvinceName || "Province", path: provinceListPath }]
            : []),
        {
            title: `${branchName} — By User`,
            path: summaryPath,
        },
        { title: userName },
    ];
    const [fetchDetail, { data: detailData, isLoading, isFetching, error: detailError }] =
        useLazyGetBranchCOCUserDetailQuery();
    const [fetchForExport, { isLoading: isExporting }] = useLazyGetBranchCOCUserDetailQuery();
    const fetchData = useCallback(() => {
        if (!branchId || !userId) {
            setErrorMessage("Missing branch or user selection.");
            return;
        }
        fetchDetail({
            branchId,
            userId,
            params: {
                page: currentPage,
                limit: perPage,
                keyword: debouncedKeyword?.trim() || undefined,
            },
        }).catch((err) => {
            setErrorMessage(err?.data?.message || "Failed to fetch user COC detail");
        });
    }, [branchId, userId, fetchDetail, currentPage, perPage, debouncedKeyword]);
    const handleExport = async () => {
        try {
            const res = await fetchForExport({
                branchId,
                userId,
                params: { page: 1, limit: 10000, keyword: debouncedKeyword?.trim() || undefined },
            }).unwrap();
            const sheetData = res.data.map((row) => ({
                "Shipment ID": row.shipmentId || "",
                "Charge": row.charge ?? 0,
                "Charge Type": row.chargeType || "",
                "Payment Status": row.paymentStatus || "",
                "Payment Location": row.paymentLocation || "",
                "Created At": row.createdAt ? moment(row.createdAt).format("MMM DD, YYYY") : "",
            }));
            exportExcelFile({ sheetData, fileName: `${userName}_COC_Detail` });
        } catch (err) {
            setErrorMessage("Failed to export data.");
        }
    };
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    useEffect(() => {
        if (detailError) setErrorMessage("Failed to fetch user COC detail.");
    }, [detailError]);
    const columns = [
        {
            name: "Shipment ID",
            selector: (row) => row.shipmentId,
            cell: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {row.shipmentId || "N/A"}
                    <Tooltip title="View shipment detail in a new tab" arrow>
                        <IconButton size="small" onClick={() => window.open(`/app/crm/shipment/${row.shipmentObjectId}/detail`, "_blank")}>
                            <OpenInNew fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
            grow: 1.5,
        },
        {
            name: "Charge",
            selector: (row) => row.charge,
            cell: (row) => `Rs. ${row.charge?.toFixed(2) || "0.00"}`,
        },
        {
            name: "Charge Type",
            selector: (row) => row.chargeType,
            cell: (row) => row.chargeType || "N/A",
        },
        {
            name: "Payment Status",
            selector: (row) => row.paymentStatus,
            cell: (row) => (
                <Chip label={row.paymentStatus || "N/A"} color={paymentStatusColor(row.paymentStatus)} size="small" variant="outlined" />
            ),
        },
        {
            name: "Payment Location",
            selector: (row) => row.paymentLocation,
            cell: (row) => row.paymentLocation || "N/A",
        },
        {
            name: "Created At",
            selector: (row) => row.createdAt,
            cell: (row) => (row.createdAt ? moment(row.createdAt).format("MMM DD, YYYY") : "N/A"),
        },
    ];
    useAppMeta(`COC Detail — ${userName}`);
    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Error error={detailError} />
            <BreadcrumbLayout.Paper>
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 2, px: 2, pt: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                            {userName} — COC Detail ({branchName})
                        </Typography>
                        <Tooltip title="Download Excel">
                            <span>
                                <IconButton color="primary" size="small" onClick={handleExport} disabled={isExporting || !detailData?.data?.length}>
                                    <Download />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
    <TextField
        size="small"
        placeholder="Search Shipment ID..."
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        sx={{ minWidth: 220 }}
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <Search fontSize="small" />
                </InputAdornment>
            ),
        }}
    />
</Box>
                </Box>
                {detailData?.totalNotPaid !== undefined && (
                    <Box sx={{ px: 2, pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Total Not Paid: <strong>Rs. {Number(detailData.totalNotPaid || 0).toFixed(2)}</strong>
                        </Typography>
                    </Box>
                )}
                {detailData?.totalItems !== undefined && (
                    <Box sx={{ px: 2, pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {detailData.totalItems} record{detailData.totalItems !== 1 ? "s" : ""} found
                        </Typography>
                    </Box>
                )}
                {isLoading || isFetching ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : detailData?.data?.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            No records found for the selected filters.
                        </Typography>
                    </Box>
                ) : (
                    <AppTable
                        columns={columns}
                        data={detailData?.data || []}
                        progressPending={isLoading || isFetching}
                        paginationServer
                        paginationPerPage={perPage}
                        paginationTotalRows={detailData?.totalItems ?? 0}
                        paginationDefaultPage={currentPage}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                    />
                )}
                <Snackbar open={!!errorMessage} autoHideDuration={6000} onClose={() => setErrorMessage("")}>
                    <Alert severity="error" onClose={() => setErrorMessage("")}>
                        {errorMessage}
                    </Alert>
                </Snackbar>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default BranchCOCUserDetail;
